import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getSupabaseUrl, getSupabaseAnonKey } from '../lib/environments';
import { returnToApp, isInKizuWebView } from '../lib/returnToApp';
import { ThemeToggle } from './ui/ThemeToggle';

export const Signup: React.FC = () => {
  const [searchParams] = useSearchParams();
  const inviteId = searchParams.get('invite');
  const inviteEmail = searchParams.get('email');
  const invitePhone = searchParams.get('phone');
  const inviteName = searchParams.get('name');
  const inviteToken = searchParams.get('invite_token');
  const personalInviteToken = searchParams.get('personal_invite');
  const connectTo = searchParams.get('connect_to');
  const inviteFrom = searchParams.get('from');
  const inviteRef = searchParams.get('ref');

  const [email, setEmail] = useState(inviteEmail || '');
  const [phone, setPhone] = useState(invitePhone || '');
  const [fullName, setFullName] = useState(inviteName || '');
  const defaultMethod: 'email' | 'phone' = invitePhone && !inviteEmail ? 'phone' : 'email';
  const [signupMethod, setSignupMethod] = useState<'email' | 'phone'>(defaultMethod);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accountCreated, setAccountCreated] = useState(false);
  const [sessionTokens, setSessionTokens] = useState<{ accessToken: string; refreshToken: string } | null>(null);
  const [debugLogs, setDebugLogs] = useState<string[]>([]);
  const [appOpenFailed, setAppOpenFailed] = useState(false);
  const [personalInviteInviterName, setPersonalInviteInviterName] = useState<string | null>(null);

  const addLog = (msg: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setDebugLogs(prev => [...prev.slice(-10), `[${timestamp}] ${msg}`]);
    console.log(msg);
  };
  const [inviteDetails, setInviteDetails] = useState<{
    circleName: string;
    role: string;
    inviterName: string;
  } | null>(null);
  const [loadingInvite, setLoadingInvite] = useState(!!inviteId);

  const { user, loading: authLoading } = useAuth();

  // Detect if user is on mobile device
  const isMobileDevice = () => {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
  };

  const isMobile = isMobileDevice();
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isAndroid = /android/i.test(navigator.userAgent);

  // Where to send the user after a successful sign-up / continue-in-browser.
  // - On mobile.* subdomain → mobile web app (same origin)
  // - On dashboard.* subdomain + mobile device → switch to mobile subdomain
  // - On dashboard.* subdomain + desktop → /admin
  // - localhost / unrecognized host: same-origin /mobile/home or /admin by device
  const getPostAuthUrl = (): string => {
    const hostname = window.location.hostname;
    if (hostname.includes('mobile.')) return '/mobile/home';
    if (isMobile && hostname.includes('dashboard.')) {
      const mobileHost = hostname.replace('dashboard.', 'mobile.');
      const port = window.location.port ? `:${window.location.port}` : '';
      return `${window.location.protocol}//${mobileHost}${port}/mobile/home`;
    }
    if (isMobile) return '/mobile/home';
    return '/admin';
  };
  const userAgent = navigator.userAgent;

  const APP_STORE_URL = 'https://apps.apple.com/us/app/kizu-mobile/id6757609621';
  const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.evolve.kizu.mobile';

  // Load circle invite details if we have an invite ID
  useEffect(() => {
    if (inviteId) {
      loadInviteDetails();
    } else {
      setLoadingInvite(false);
    }
  }, [inviteId]);

  // Load link invite details (phone/email/inviter) from token
  const [linkInviteFrom, setLinkInviteFrom] = useState<string | null>(inviteFrom || null);
  const [linkInviteAvatar, setLinkInviteAvatar] = useState<string | null>(null);
  // Track the invite's original email/phone so we can detect when the user
  // edits them and force a verification step before we create the account.
  const [originalInviteEmail, setOriginalInviteEmail] = useState<string | null>(null);
  const [originalInvitePhone, setOriginalInvitePhone] = useState<string | null>(null);
  // Verification flow state — only used when the user edits the invite's
  // identifier and we have to send a real OTP before creating the account.
  const [verificationMode, setVerificationMode] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [tokenDebug, setTokenDebug] = useState<string>('');
  useEffect(() => {
    if (!inviteToken) {
      setTokenDebug('no invite_token param');
      return;
    }
    setTokenDebug(`fetching token ${inviteToken}...`);
    const fetchLinkInvite = async () => {
      try {
        const url = `${getSupabaseUrl()}/functions/v1/share-invite-page?token=${inviteToken}`;
        setTokenDebug(`fetching: ${url}`);
        const res = await fetch(url);
        const text = await res.text();
        setTokenDebug(`status: ${res.status}, body: ${text.substring(0, 100)}`);
        if (!res.ok) return;
        const data = JSON.parse(text);
        if (data.phone) {
          setPhone(data.phone);
          setOriginalInvitePhone(data.phone);
          setSignupMethod('phone');
        }
        if (data.email) {
          setEmail(data.email);
          setOriginalInviteEmail(data.email);
          setSignupMethod('email');
        }
        // Prefill the recipient's name from the invite. The mobile inviter
        // captures it from their address book at send time; without this
        // the new user has to retype something the inviter already knew.
        // Only set if the user hasn't already typed anything (so we don't
        // clobber an in-progress edit).
        if (data.invitee_name) {
          setFullName((current) => current || data.invitee_name);
        }
        if (data.inviter_name) {
          setLinkInviteFrom(data.inviter_name);
        }
        if (data.inviter_avatar) {
          setLinkInviteAvatar(data.inviter_avatar);
        }
        setTokenDebug(`done: phone=${data.phone}, email=${data.email}, inviter=${data.inviter_name}, invitee=${data.invitee_name || ''}`);
      } catch (err: any) {
        setTokenDebug(`error: ${err?.message || err}`);
      }
    };
    fetchLinkInvite();
  }, [inviteToken]);

  const loadInviteDetails = async () => {
    try {
      // Fetch invite details via API (doesn't require auth)
      const response = await fetch(
        `${getSupabaseUrl()}/functions/v1/view-circle-api?invite_id=${inviteId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'apikey': getSupabaseAnonKey(),
          },
        }
      );

      const data = await response.json();
      if (response.ok && data.success && data.invite) {
        const invite = data.invite;
        setInviteDetails({
          circleName: invite.circles?.name || 'a photo circle',
          role: invite.role,
          inviterName: invite.circles?.profiles?.full_name || 'Someone',
        });
        // Pre-fill email if available from invite (and not already set from URL)
        if (invite.email && !email) {
          setEmail(invite.email);
        }
      } else {
        // API returned error - show generic invitation message
        console.log('Could not load invite details, proceeding with generic signup');
      }
    } catch (err) {
      console.log('Could not load invite details:', err);
      // Continue without invite details - user can still sign up
    } finally {
      setLoadingInvite(false);
    }
  };

  // Don't wait for auth loading on signup page - new users won't have a session
  // Just check if user is already logged in (after auth loads)

  // If user is already logged in AND we haven't just created an account, redirect
  // But NOT if they arrived via an invite link — they may need to create a new account
  if (user && !accountCreated && !inviteToken && !personalInviteToken && !connectTo && !inviteId) {
    window.location.href = getPostAuthUrl();
    return null;
  }

  // Did the user edit the email/phone away from the invite's? If so we
  // can't trust the SMS/email "click" as proof of ownership and must run
  // a real OTP loop on the new identifier before creating the account.
  const identifierChanged = (() => {
    if (!inviteToken) return false;
    if (signupMethod === 'email') {
      return !!originalInviteEmail && email.trim().toLowerCase() !== originalInviteEmail.toLowerCase();
    }
    return !!originalInvitePhone && phone.replace(/\D/g, '') !== originalInvitePhone.replace(/\D/g, '');
  })();

  // Send an OTP to the new identifier — used when the user edits the
  // invite's prefilled email/phone.
  const sendVerificationCode = async (): Promise<boolean> => {
    addLog('Sending verification code…');
    try {
      // Routed through /api/send-verification-code (Vercel function with
      // BotID check) instead of hitting Supabase directly — see
      // src/main.tsx initBotId() and api/send-verification-code.ts.
      const res = await fetch(`/api/send-verification-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: signupMethod === 'email' ? email.toLowerCase().trim() : undefined,
          phone: signupMethod === 'phone' ? phone.trim() : undefined,
          identifier_type: signupMethod === 'phone' ? 'phone' : 'email',
          fullName: fullName.trim() || null,
          intent: 'signup',
          type: 'signup',
        }),
      });
      const json = await res.json();
      if (!res.ok || json?.success === false) {
        setError(json?.error || 'Failed to send verification code.');
        return false;
      }
      addLog('Verification code sent');
      return true;
    } catch (err: any) {
      setError(err?.message || 'Failed to send verification code.');
      return false;
    }
  };

  // Submit the OTP the user entered to complete signup. verify-code-auth
  // creates the auth user, attaches profile metadata, and processes the
  // invite_token to link them to the inviter.
  const handleVerifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!verificationCode.trim()) return;
    setVerificationLoading(true);
    setError(null);

    const redirectPath = inviteId ? `/view-circle/${inviteId}` : getPostAuthUrl();
    sessionStorage.setItem('postLoginRedirect', redirectPath);

    try {
      const supabaseUrl = getSupabaseUrl();
      const supabaseAnonKey = getSupabaseAnonKey();
      const res = await fetch(`${supabaseUrl}/functions/v1/verify-code-auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: supabaseAnonKey,
          Authorization: `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({
          email: signupMethod === 'email' ? email.toLowerCase().trim() : undefined,
          phone: signupMethod === 'phone' ? phone.trim() : undefined,
          identifier_type: signupMethod === 'phone' ? 'phone' : 'email',
          code: verificationCode.trim(),
          invite_token: inviteToken || undefined,
        }),
      });
      const json = await res.json();
      if (!res.ok || json?.success === false) {
        setError(json?.error || 'Invalid code. Please try again.');
        setVerificationLoading(false);
        return;
      }

      // verify-code-auth returns session tokens on success — surface the
      // success screen the same way the SIGNUP shortcut does.
      const accessToken = json?.session?.access_token || json?.data?.session?.access_token;
      const refreshToken = json?.session?.refresh_token || json?.data?.session?.refresh_token;
      if (accessToken && refreshToken) {
        setSessionTokens({ accessToken, refreshToken });
        setAccountCreated(true);
      } else {
        setError('Account created but session is missing. Please log in manually.');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to verify code.');
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Store redirect path before creating account
    const redirectPath = inviteId ? `/view-circle/${inviteId}` : getPostAuthUrl();
    sessionStorage.setItem('postLoginRedirect', redirectPath);

    addLog(`Starting signup for ${signupMethod === 'phone' ? phone : email} (mobile: ${isMobile})`);

    // Identifier was edited away from the invite — skip the SIGNUP
    // shortcut and go through a real verification loop instead.
    if (identifierChanged) {
      const sent = await sendVerificationCode();
      setLoading(false);
      if (sent) setVerificationMode(true);
      return;
    }

    try {
      // Use direct fetch with proper timeout support
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        addLog('Request timed out after 30s, aborting...');
        controller.abort();
      }, 30000);

      const supabaseUrl = getSupabaseUrl();
      const supabaseAnonKey = getSupabaseAnonKey();

      addLog('Calling edge function...');

      const response = await fetch(`${supabaseUrl}/functions/v1/create-session-with-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({
          email: signupMethod === 'email' ? email.toLowerCase().trim() : undefined,
          phone: signupMethod === 'phone' ? phone.trim() : undefined,
          code: 'SIGNUP',
          full_name: fullName.trim() || null,
          invite_token: inviteToken || undefined,
          connect_to: connectTo || undefined,
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      addLog(`Response received: ${response.status}`);

      const authResponse = await response.json();
      addLog(`Parsed response: ${authResponse.success ? 'success' : 'failed'}`);

      // The server's SIGNUP-shortcut guard returns 409 when the
      // submitted email/phone differs from what's on the invite — fall
      // back to a real OTP loop in that case.
      if (response.status === 409 && authResponse?.error === 'verification_required') {
        addLog('Server demanded verification — sending code');
        const sent = await sendVerificationCode();
        setLoading(false);
        if (sent) setVerificationMode(true);
        return;
      }

      if (!response.ok || !authResponse?.success) {
        console.error('Account creation failed', authResponse);
        setError(authResponse?.error || 'Failed to create account. Please try again.');
        setLoading(false);
        return;
      }

      // Store tokens and show success screen
      if (authResponse.session?.access_token && authResponse.session?.refresh_token) {
        addLog('Got session tokens, showing success screen');
        setSessionTokens({
          accessToken: authResponse.session.access_token,
          refreshToken: authResponse.session.refresh_token,
        });

        // If signup carried a permanent personal-invite token, consume it now
        // so the new user is auto-connected with the inviter and the welcome
        // chat message lands. Errors here are non-fatal — signup succeeded
        // and the user can connect manually later.
        if (personalInviteToken) {
          try {
            const acceptRes = await fetch(
              `${supabaseUrl}/functions/v1/links-api?action=accept-personal-invite`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  apikey: supabaseAnonKey,
                  Authorization: `Bearer ${authResponse.session.access_token}`,
                },
                body: JSON.stringify({ token: personalInviteToken }),
              },
            );
            const acceptJson = await acceptRes.json().catch(() => ({}));
            const inviterName = (acceptJson?.data ?? acceptJson)?.inviter_name;
            if (acceptRes.ok && inviterName) {
              setPersonalInviteInviterName(inviterName);
              addLog(`personal-invite accepted (inviter: ${inviterName})`);
            } else {
              addLog(`personal-invite accept skipped: ${acceptRes.status}`);
            }
          } catch (acceptErr: any) {
            addLog(`personal-invite accept error: ${acceptErr?.message || acceptErr}`);
          }
        }

        setAccountCreated(true);
        setLoading(false);
      } else {
        addLog('No session tokens in response');
        setError('Account created but missing session. Please try logging in manually.');
        setLoading(false);
      }
    } catch (err: any) {
      console.error('Signup error', err);
      addLog(`Error: ${err.name} - ${err.message}`);
      if (err.name === 'AbortError') {
        setError('Request timed out. Please check your connection and try again.');
      } else {
        setError(`Error: ${err.message || 'An unexpected error occurred'}`);
      }
      setLoading(false);
    }
  };

  // Handle opening the mobile app
  const handleOpenMobileApp = () => {
    if (!sessionTokens) return;

    const tokenData = JSON.stringify({
      access_token: sessionTokens.accessToken,
      refresh_token: sessionTokens.refreshToken
    });
    // Use URL-safe base64 encoding (replace +/= with URL-safe chars)
    const base64Tokens = btoa(tokenData);
    const urlSafeTokens = base64Tokens.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');

    addLog('Attempting to open app via deep link...');

    // Inside the WebView the app is already open — just close the modal.
    if (isInKizuWebView()) {
      returnToApp();
      return;
    }

    // If the app opens, the page loses visibility. Track that.
    let appOpened = false;
    const onVisibilityChange = () => {
      if (document.hidden) {
        appOpened = true;
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    // Try the deep link
    returnToApp({ path: `auth-session/${urlSafeTokens}` });

    // After 1.5s, if page is still visible, app didn't open
    setTimeout(() => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (!appOpened) {
        addLog('App did not open, showing download options');
        setAppOpenFailed(true);
      }
    }, 1500);
  };

  // Handle continuing to web dashboard
  const handleContinueToWeb = async () => {
    if (!sessionTokens) return;

    setLoading(true);
    addLog('Setting session...');

    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
        access_token: sessionTokens.accessToken,
        refresh_token: sessionTokens.refreshToken,
      });

      if (sessionError) {
        addLog(`setSession error: ${sessionError.message}`);
        setError('Sign-in failed. Please try logging in manually.');
        setLoading(false);
        return;
      }

      if (!sessionData.session || !sessionData.user) {
        addLog('No session returned from setSession');
        setError('Sign-in failed. Please try logging in manually.');
        setLoading(false);
        return;
      }

      addLog(`Session set for user: ${sessionData.user.id}`);

      // Wait for session to persist to storage before redirecting
      await new Promise(resolve => setTimeout(resolve, 500));

      // Verify session persisted
      const { data: { session: verifiedSession } } = await supabase.auth.getSession();
      if (!verifiedSession) {
        addLog('Session did not persist after setSession');
        setError('Sign-in failed. Please try logging in manually.');
        setLoading(false);
        return;
      }

      const dest = getPostAuthUrl();
      addLog(`Redirecting to: ${dest} (mobile device: ${isMobile})`);
      window.location.replace(dest);
    } catch (err: any) {
      addLog(`Continue error: ${err?.message}`);
      setError('Sign-in failed. Please try logging in manually.');
      setLoading(false);
    }
  };

  const getRoleDisplay = (role: string) => {
    const roleMap: Record<string, string> = {
      admin: 'Admin',
      editor: 'Editor',
      contributor: 'Contributor',
      read_only: 'Viewer',
    };
    return roleMap[role] || role;
  };

  const getRoleBadgeClass = (role: string) => {
    const classMap: Record<string, string> = {
      admin: 'badge-error',
      editor: 'badge-warning',
      contributor: 'badge-success',
      read_only: 'badge-default',
    };
    return classMap[role] || 'badge-default';
  };

  // Don't block render for invite loading - show form immediately

  return (
    <div className="min-h-screen auth-gradient flex justify-center p-4 pt-16 relative">
      {/* Theme toggle in corner */}
      <div className="absolute top-4 right-4">
        <ThemeToggle size="sm" />
      </div>

      <div className="auth-card p-8 w-full max-w-md h-fit">
        <div className="text-center mb-6">
          {inviteToken && linkInviteAvatar ? (
            <img
              src={linkInviteAvatar}
              alt={linkInviteFrom || 'Inviter'}
              className="w-24 h-24 rounded-full mx-auto mb-4 object-cover border-2 border-theme-primary/20"
            />
          ) : (
            <div className="text-4xl mb-4">📸</div>
          )}
          <h1 className="text-3xl font-bold text-theme-primary">Join Kizu</h1>
          {/* deploy marker: v2 */}
          {inviteDetails ? (
            <p className="text-theme-secondary mt-2">
              {inviteDetails.inviterName} invited you to join{' '}
              <strong>{inviteDetails.circleName}</strong>
            </p>
          ) : inviteId ? (
            <p className="text-theme-secondary mt-2">
              You've been invited to join a photo circle. Create your account to get started.
            </p>
          ) : inviteToken && linkInviteFrom ? (
            <p className="text-theme-secondary mt-2">
              <strong>{linkInviteFrom}</strong> wants to connect with you on Kizu
            </p>
          ) : inviteToken ? (
            <p className="text-theme-secondary mt-2">
              You've been invited to join Kizu
            </p>
          ) : personalInviteToken ? (
            <p className="text-theme-secondary mt-2">
              You've been invited to join Kizu — sign up to connect with whoever shared their QR with you.
            </p>
          ) : (linkInviteFrom || inviteRef) ? (
            <p className="text-theme-secondary mt-2">
              {linkInviteFrom || inviteRef} wants to connect with you on Kizu — where your digital memories live
            </p>
          ) : (
            <p className="text-theme-secondary mt-2">
              Create your account to get started
            </p>
          )}
        </div>

        {/* Verification Screen — user changed the invite's email/phone
            so we sent an OTP and need them to enter it before creating
            the account. */}
        {verificationMode && !accountCreated ? (
          <form onSubmit={handleVerifySubmit} className="space-y-4">
            <p className="text-theme-secondary text-sm">
              We sent a verification code to{' '}
              <strong>{signupMethod === 'email' ? email : phone}</strong>.
              Enter it below to finish creating your account.
            </p>
            <div>
              <label htmlFor="verificationCode" className="form-label">
                Verification Code
              </label>
              <input
                id="verificationCode"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.toUpperCase())}
                required
                autoFocus
                className="input placeholder-muted tracking-widest text-center text-lg"
                placeholder="• • • • •"
                maxLength={6}
              />
            </div>
            {error && <div className="alert-error">{error}</div>}
            <button
              type="submit"
              disabled={verificationLoading || !verificationCode.trim()}
              className="w-full btn-primary py-3 flex items-center justify-center gap-2"
            >
              {verificationLoading ? 'Verifying…' : 'Verify & Join Kizu'}
            </button>
            <button
              type="button"
              onClick={() => {
                setVerificationMode(false);
                setVerificationCode('');
                setError(null);
              }}
              className="w-full text-sm link-primary"
            >
              Use a different {signupMethod === 'email' ? 'email' : 'phone number'}
            </button>
          </form>
        ) : accountCreated ? (
          <div className="text-center space-y-5">
            <div className="text-5xl">🎉</div>
            <div>
              <h2 className="text-xl font-bold text-theme-primary mb-1">You're in!</h2>
              <p className="text-theme-secondary text-sm">
                Welcome to Kizu{fullName ? `, ${fullName}` : ''}. Your account is ready.
              </p>
              {personalInviteInviterName && (
                <p className="text-theme-secondary text-sm mt-2">
                  You're now connected with <strong>{personalInviteInviterName}</strong>.
                  Open the chat to see their welcome message.
                </p>
              )}
            </div>

            {isMobile ? (
              <div className="space-y-4">
                {/* Primary: Open in App */}
                <button
                  onClick={handleOpenMobileApp}
                  className="w-full btn-primary py-4 text-lg font-semibold rounded-xl flex items-center justify-center gap-3"
                >
                  <span className="text-2xl">📱</span>
                  Open in Kizu App
                </button>

                {/* Download the App */}
                <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl space-y-3">
                  <p className="text-sm font-medium text-theme-primary">
                    {appOpenFailed ? "Don't have the app yet?" : 'Get the Kizu app'}
                  </p>
                  <div className="flex flex-col gap-3 items-center">
                    {isIOS ? (
                      <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer"
                         className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-black rounded-xl text-white font-medium">
                        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                        Download for iPhone
                      </a>
                    ) : isAndroid ? (
                      <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer"
                         className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-black rounded-xl text-white font-medium">
                        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor"><path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.807 1.626a1 1 0 010 1.732l-2.808 1.626L15.206 12l2.492-2.492zM5.864 2.658L16.8 8.99l-2.3 2.3-8.636-8.632z"/></svg>
                        Download for Android
                      </a>
                    ) : (
                      <div className="flex gap-3">
                        <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                          <img src="https://www.kizu.online/app-store-badge.png" alt="App Store" height="44" />
                        </a>
                        <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
                          <img src="https://www.kizu.online/google-play-badge.png" alt="Google Play" height="44" />
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* Continue in browser */}
                <button
                  onClick={handleContinueToWeb}
                  disabled={loading}
                  className="w-full py-3 rounded-xl border border-gray-300 dark:border-gray-600 text-theme-secondary hover:text-theme-primary transition-colors text-sm font-medium"
                >
                  {loading ? 'Loading...' : 'Continue in browser instead'}
                </button>
              </div>
            ) : (
              <button
                onClick={handleContinueToWeb}
                disabled={loading}
                className="w-full btn-primary py-4 text-lg font-semibold"
              >
                {loading ? 'Signing in...' : 'Continue to Dashboard'}
              </button>
            )}

            {error && (
              <div className="alert-error">
                {error}
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Invitation details card */}
            {inviteDetails && (
              <div className="badge-primary p-4 rounded-lg mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{inviteDetails.circleName}</p>
                    <p className="text-sm opacity-80">Photo Circle</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRoleBadgeClass(inviteDetails.role)}`}>
                    {getRoleDisplay(inviteDetails.role)}
                  </span>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="fullName" className="form-label">
              Full Name
            </label>
            <input
              id="fullName"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              className="input placeholder-muted"
              placeholder="Your name"
            />
          </div>

          {signupMethod === 'phone' ? (
          <div>
            <label htmlFor="phone" className="form-label">
              Phone Number
            </label>
            <input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              className="input placeholder-muted"
              placeholder="+1 (555) 123-4567"
            />
            <button
              type="button"
              onClick={() => setSignupMethod('email')}
              className="text-sm link-primary mt-2"
            >
              Use email instead
            </button>
          </div>
          ) : (
          <div>
            <label htmlFor="email" className="form-label">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="input placeholder-muted"
              placeholder="your@email.com"
            />
            {phone && (
              <button
                type="button"
                onClick={() => setSignupMethod('phone')}
                className="text-sm link-primary mt-2"
              >
                Use phone number instead
              </button>
            )}
          </div>
          )}

          {error && (
            <div className="alert-error">
              {error}
            </div>
          )}

          {/* When the user edits the prepopulated email/phone, the new
              identifier hasn't been verified by the SMS/email click —
              warn them so they're not surprised by a verification step
              once we wire it up server-side. */}
          {inviteToken && (
            (signupMethod === 'email' && originalInviteEmail && email.trim().toLowerCase() !== originalInviteEmail.toLowerCase()) ||
            (signupMethod === 'phone' && originalInvitePhone && phone.replace(/\D/g, '') !== originalInvitePhone.replace(/\D/g, ''))
          ) && (
            <div className="alert-warning text-sm">
              You changed the {signupMethod === 'email' ? 'email' : 'phone number'} from
              the one your invite was sent to — we'll send a verification code to confirm it.
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !fullName || (signupMethod === 'email' ? !email : !phone)}
            className="w-full btn-primary py-3 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Creating account...
              </>
            ) : inviteToken ? (
              'Join Kizu'
            ) : (
              'Create Account'
            )}
          </button>
            </form>

            <div className="mt-6 text-center text-sm text-theme-secondary">
              <p>
                Already have an account?{' '}
                <a
                  href={inviteId ? `/login?redirect=/view-circle/${inviteId}` : '/login'}
                  className="link-primary underline"
                >
                  Sign in
                </a>
              </p>
            </div>

            {/* Debug info */}
            {window.location.hostname !== 'dashboard.kizu.online' && (
            <div className="mt-4 p-3 bg-gray-100 dark:bg-gray-800 rounded text-xs text-gray-600 dark:text-gray-400">
              <p><strong>Device:</strong> {isMobile ? '📱 Mobile' : '💻 Desktop'}</p>
              <p className="mt-1 break-all"><strong>UA:</strong> {userAgent.substring(0, 100)}...</p>
              {tokenDebug && <p className="mt-1 break-all"><strong>Token:</strong> {tokenDebug}</p>}
            </div>
            )}
          </>
        )}

        {/* Debug log panel */}
        {debugLogs.length > 0 && window.location.hostname !== 'dashboard.kizu.online' && (
          <div className="mt-4 p-3 bg-black text-green-400 rounded text-xs font-mono max-h-40 overflow-y-auto">
            <p className="text-white mb-1 font-bold">Debug Log:</p>
            {debugLogs.map((log, i) => (
              <p key={i} className="break-all">{log}</p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
